import { useEffect } from 'react'
import type { JSX } from 'react'

import {
  Button,
  ChevronDownIcon,
  DropdownMenu,
  Flex,
} from '@buffer-mono/popcorn'

import { AllChannelsTimeZoneSelect } from '~publish/components/AllChannelsTimeZoneSelect'
import type { MaskedFilterByChannelFragment } from '~publish/components/FilterByChannel'
import {
  FilterByChannel,
  splitNoChannelsSelection,
  useFilterByChannelFragment,
} from '~publish/components/FilterByChannel'
import { FilterByDateRange } from '~publish/components/FilterByDateRange'
import { FilterByStatus } from '~publish/components/FilterByStatus'
import { FilterByTag } from '~publish/components/FilterByTag'

import {
  getDisabledChannelIds,
  getDisabledPublishChannelReason,
} from '~publish/utils/permissions/getDisabledChannelIds'

import type { BoardGroupBy, BoardState } from './useBoardState'
import { useTrackedBoardFilters } from './useTrackedBoardFilters'

import styles from './BoardFilterBar.module.css'

const GROUP_BY_LABELS: Record<BoardGroupBy, string> = {
  status: 'Status',
  tag: 'Tag',
  channel: 'Channel',
}

function isBoardGroupBy(value: string): value is BoardGroupBy {
  return value === 'status' || value === 'tag' || value === 'channel'
}

type BoardFilterBarProps = {
  state: BoardState
  channels: MaskedFilterByChannelFragment
}

export function BoardFilterBar({
  state,
  channels,
}: BoardFilterBarProps): JSX.Element {
  const channelData = useFilterByChannelFragment(channels)
  const trackedFilters = useTrackedBoardFilters(state)
  const disabledChannelIds = getDisabledChannelIds(channelData)
  // Only the Status-grouped Draft column reads this filter — hide it once a
  // Status filter excludes Draft, so it's never a no-op.
  const showNoChannelsOption =
    state.groupBy === 'status' &&
    (state.statuses.length === 0 || state.statuses.includes('draft'))

  // Clear a lingering "No channels" selection once the row hides — otherwise
  // it stays checked (and counted) with no way to uncheck it until the user
  // returns to a groupBy/Status combination where the row is visible again.
  // `replace: true` corrects the URL in place, so it doesn't push a history
  // entry the user would have to fight through on Back.
  const { channelIds, setChannelIds } = state
  useEffect(() => {
    if (showNoChannelsOption) return
    const { isNoChannelsSelected, realChannelIds } =
      splitNoChannelsSelection(channelIds)
    if (isNoChannelsSelected) {
      setChannelIds(realChannelIds, { replace: true })
    }
  }, [showNoChannelsOption, channelIds, setChannelIds])

  return (
    <Flex
      justify="between"
      align="center"
      wrap="wrap"
      gap="sm"
      className={styles.filterBar}
    >
      <DropdownMenu>
        <DropdownMenu.Trigger>
          <Button variant="tertiary" size="medium">
            Group by: {GROUP_BY_LABELS[state.groupBy]}
            <ChevronDownIcon size="xsmall" color="subtle" />
          </Button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content align="start">
          <DropdownMenu.RadioGroup
            value={state.groupBy}
            onChange={(value): void => {
              if (isBoardGroupBy(value)) trackedFilters.setGroupBy(value)
            }}
          >
            <DropdownMenu.RadioItem value="status">
              Status
            </DropdownMenu.RadioItem>
            <DropdownMenu.RadioItem value="tag">Tag</DropdownMenu.RadioItem>
            <DropdownMenu.RadioItem value="channel">
              Channel
            </DropdownMenu.RadioItem>
          </DropdownMenu.RadioGroup>
        </DropdownMenu.Content>
      </DropdownMenu>

      <Flex gap="xs" wrap="wrap" className={styles.filters}>
        <FilterByChannel
          channels={channels}
          disabledChannelIds={disabledChannelIds}
          getDisabledChannelReason={(id) =>
            getDisabledPublishChannelReason(
              channelData.find((channel) => channel.id === id),
            )
          }
          value={state.channelIds}
          onSelect={trackedFilters.setChannelIds}
          showNoChannelsOption={showNoChannelsOption}
        />
        <FilterByTag value={state.tagIds} onSelect={trackedFilters.setTagIds} />
        <FilterByStatus
          value={state.statuses}
          onSelect={trackedFilters.setStatuses}
        />
        <FilterByDateRange
          value={state.dateRange}
          onApply={trackedFilters.setDateRange}
          onClear={trackedFilters.clearDateRange}
        />
        <AllChannelsTimeZoneSelect />
      </Flex>
    </Flex>
  )
}
